import React, { useCallback } from "react";

import cx from "classnames";

import { RadioInput } from "~/feedback/FormInputs";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import "./prompts/prompts.css";

export type ChoiceModalOption = {
	value: string;
	label: string;
};

export type ChoiceModalResponse = ChoiceModalOption | null;

type ChoiceModalProps = {
	// Since this component wrapped into `__globalModalContext.open`, the `options` prop cannot be used,
	// and would be redefined. So using `choices` instead.
	choices: ChoiceModalOption[];
	title: string;
	cancelText?: string;
	selectText?: string;
};

type ComponentProps = ChoiceModalProps & {
	resolve: (result: ChoiceModalResponse) => void;
};

function ChoiceModalComponent(props: ComponentProps) {
	const { choices, title, cancelText = "Cancel", selectText = "Ok", resolve } = props;
	const options = choices;

	const [selected, setSelected] = React.useState(options.length > 0 ? options[0].value : "");
	const handleChange = useCallback(
		(val: string) => {
			const selectedOption = options.find(o => o.value === val);
			if (selectedOption) {
				setSelected(val);
			}
		},
		[options],
	);

	const selectedOption = options.find(o => o.value === selected);

	const handleClose = useCallback(() => {
		resolve(null);
	}, [resolve]);

	const handleCancel = useCallback(() => {
		resolve(null);
	}, [resolve]);

	const handleSelect = useCallback(() => {
		if (selectedOption) resolve(selectedOption);
	}, [resolve, selectedOption]);

	if (!selectedOption) {
		return null;
	}

	return (
		<Modal onClose={handleClose} className={cx("prompt", "simple")}>
			<Modal.Header onClose={handleClose}>
				<Modal.Title>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="prompt__body">
				<div className="prompt__choice__items">
					<RadioInput
						name={selectedOption.label}
						value={selectedOption.value}
						options={options}
						onChange={handleChange}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={handleCancel}>
					{cancelText}
				</Button>
				<Button size="normal" variant="primary" onClick={handleSelect}>
					{selectText}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export const choiceModal = (options: ChoiceModalProps) => {
	if (!__globalModalContext) {
		return Promise.resolve(null);
	}

	return __globalModalContext.open<ChoiceModalResponse>(({ close }) => {
		return <ChoiceModalComponent {...options} resolve={close} />;
	});
};
